import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, FileText, FileSpreadsheet } from "lucide-react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";

const DataGridSidebar = ({
  selectedProduct,
  columns,
  rows,
  title = "Data Report",
  showToggle = true,
  showDepartmentFilter = true,
  showExport = true,
  pageSize = 5,
  pageSizeOptions = [5, 10],
  height = 400,
  handleDepartmentChange,
  onToggleChange,
  exportFileName = "report",
  onToggleTrYear,
  currentTrYear,
  departmentList = [],
  selectedDepartment,
}) => {
  const [isToggleOn, setIsToggleOn] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  const dropdownRef = useRef(null);
  const isPrior = currentTrYear === "Prior";
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    const newToggleState = !isToggleOn;
    setIsToggleOn(newToggleState);
    if (onToggleChange) {
      onToggleChange(newToggleState);
    }
  };

  const handleExportClick = () => {
    setShowExportDropdown(!showExportDropdown);
  };

  const getExportData = () => {
    return rows.map((row) => {
      const exportRow = {};
      columns.forEach((col) => {
        if (col.field && row[col.field] !== undefined) {
          exportRow[col.field] = row[col.field];
        }
      });
      return exportRow;
    });
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    // Title
    doc.setFontSize(16);
    doc.text(exportFileName, 14, 22);

    if (selectedProduct) {
      doc.setFontSize(12);
      doc.text(`Product: ${selectedProduct.Code}`, 14, 32);
    }

    //  table headers from columns
    const tableHead = [columns.map((col) => col.headerName || col.field)];

    //  table body from rows
    const tableBody = getExportData().map((row) =>
      columns.map((col) => row[col.field] || "")
    );

    autoTable(doc, {
      head: tableHead,
      body: tableBody,
      startY: selectedProduct ? 40 : 30,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
      },
      theme: "striped",
    });

    const fileName = `${exportFileName}${
      selectedProduct ? `-${selectedProduct.Code}` : ""
    }.pdf`;
    doc.save(fileName);
    setShowExportDropdown(false);
  };

  const exportToCSV = () => {
    const exportData = getExportData();
    const csvContent = [
      // Header row
      columns.map((col) => col.headerName || col.field),
      // Data rows
      ...exportData.map((row) => columns.map((col) => row[col.field] || "")),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const fileName = `${exportFileName}${
      selectedProduct ? `-${selectedProduct.code}` : ""
    }.csv`;
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportDropdown(false);
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 w-full">
        <div>
          <h3 className="text-md font-medium text-gray-800">
            {selectedProduct && <p>{selectedProduct.Code}</p>}
          </h3>
        </div>

        <div className="flex flex-wrap gap-3 items-center lg:justify-end flex-1 min-w-0">
          <button
            onClick={onToggleTrYear}
            className="bg-blue-400 p-1 rounded text-white"
          >
            {isPrior ? "Current Year" : "Previous Year"}
          </button>

          {/* Toggle Switch */}
          {/* {showToggle && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {isToggleOn ? "Hide All" : "Show All"}
              </span>
              <button
                onClick={handleToggle}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  isToggleOn ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    isToggleOn ? "translate-x-5" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          )} */}

          {/* Department Filter */}
          {showDepartmentFilter && (
            <FormControl
              size="small"
              className="flex-1 max-w-28 border"
              sx={{ minWidth: "80px" }}
            >
              <InputLabel>Department</InputLabel>
              <Select
                value={selectedDepartment}
                onChange={handleDepartmentChange}
                label="Department"
              >
                {departmentList?.map((department) => (
                  <MenuItem
                    key={department.DEPTNO}
                    value={department?.DEPTNO?.trim()}
                  >
                    {department?.DEPTNO?.trim() === "-" ? "All" : department?.DEPTNO?.trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Export Button */}
          {showExport && (
            <div className="relative" ref={dropdownRef}>
              <Tooltip
                title={rows.length === 0 ? "No data to export" : "Export Data"}
                placement="top"
              >
                <button
                  onClick={handleExportClick}
                  className="px-3 py-1 text-white text-sm font-medium bg-green-600 hover:bg-green-700 rounded-md transition-colors flex items-end"
                  disabled={rows.length === 0}
                  style={{
                    opacity: rows.length === 0 ? 0.5 : 1,
                    cursor: rows.length === 0 ? "not-allowed" : "pointer",
                  }}
                >
                  Export
                  <ChevronDown className="h-4 w-4 ml-1" />
                </button>
              </Tooltip>

              {showExportDropdown && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                  <div className="py-1">
                    <button
                      onClick={exportToPDF}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <FileText className="h-4 w-4 text-red-500" />
                      Export as PDF
                    </button>
                    <button
                      onClick={exportToCSV}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <FileSpreadsheet className="h-4 w-4 text-green-500" />
                      Export as CSV
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div>
        <Box sx={{ height: height, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: pageSize,
                },
              },
            }}
            rowHeight={38}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            pageSizeOptions={pageSizeOptions}
            disableColumnResize={false}
            sx={{
              "& .MuiDataGrid-columnSeparator": {
                visibility: "visible",
              },
            }}
          />
        </Box>
      </div>
    </div>
  );
};

export default DataGridSidebar;
